import { useState, useEffect } from "react";
import { Menu, X, Instagram } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const LOGO_WHITE = "https://customer-assets.emergentagent.com/job_yago-aesthetic/artifacts/roi08gcu_01.png";
const LOGO_DARK = "https://customer-assets.emergentagent.com/job_yago-aesthetic/artifacts/vsq23b13_02.png";

const NAV_LINKS = [
  { label: "Procedimentos", href: "#procedimentos" },
  { label: "Sobre", href: "#sobre" },
  { label: "FAQ", href: "#faq" },
  { label: "Localização", href: "#localizacao" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const scrollToSection = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/[0.78] backdrop-blur-2xl shadow-[0_1px_20px_rgba(1,30,72,0.06)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between h-[72px] md:h-[88px]">
        <a href="#hero" onClick={(e) => { e.preventDefault(); scrollToSection("#hero"); }}>
          <img
            src={scrolled ? LOGO_DARK : LOGO_WHITE}
            alt="Dr. Yago Almeida"
            className="h-12 md:h-[68px] w-auto object-contain"
            data-testid="header-logo"
          />
        </a>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-10" data-testid="desktop-nav">
          {NAV_LINKS.map((link) => (
            <button
              key={link.href}
              onClick={() => scrollToSection(link.href)}
              className={`font-body text-[11px] font-semibold tracking-[0.14em] uppercase transition-colors duration-300 ${
                scrolled ? "text-navy/80 hover:text-navy" : "text-white/75 hover:text-white"
              }`}
              data-testid={`nav-${link.href.replace("#", "")}`}
            >
              {link.label}
            </button>
          ))}
          <button
            onClick={() => scrollToSection("#contato")}
            className="bg-gold text-white text-[11px] font-body font-semibold tracking-[0.14em] uppercase px-7 py-2.5 hover:bg-gold/85 transition-all duration-300"
            data-testid="nav-cta-button"
          >
            Agendar Consulta
          </button>
          <a
            href="https://instagram.com/dryagoalmeida"
            target="_blank"
            rel="noopener noreferrer"
            className={`transition-colors duration-300 ${scrolled ? "text-navy/60 hover:text-navy" : "text-white/60 hover:text-white"}`}
            data-testid="nav-instagram"
          >
            <Instagram size={18} />
          </a>
        </nav>

        {/* Mobile hamburger */}
        <button
          className="md:hidden relative z-50"
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen ? (
            <X size={24} className="text-white" />
          ) : (
            <Menu size={24} className={scrolled ? "text-navy" : "text-white"} />
          )}
        </button>
      </div>

      {/* Mobile menu — premium navy fullscreen */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden fixed inset-0 bg-navy z-40"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col justify-center items-center h-full px-8 gap-10">
              {NAV_LINKS.map((link, idx) => (
                <motion.button
                  key={link.href}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + idx * 0.06 }}
                  onClick={() => scrollToSection(link.href)}
                  className="font-heading text-white text-3xl font-normal tracking-wide"
                  data-testid={`mobile-nav-${link.href.replace("#", "")}`}
                >
                  {link.label}
                </motion.button>
              ))}

              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="flex flex-col items-center gap-6 mt-4"
              >
                <button
                  onClick={() => scrollToSection("#contato")}
                  className="bg-gold text-white font-body font-semibold text-xs tracking-[0.15em] uppercase px-10 py-4"
                  data-testid="mobile-cta-button"
                >
                  Agendar Consulta
                </button>
                <a
                  href="https://instagram.com/dryagoalmeida"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-white/40 hover:text-white/60 transition-colors"
                  data-testid="mobile-instagram"
                >
                  <Instagram size={16} />
                  <span className="font-body text-xs tracking-wide">@dryagoalmeida</span>
                </a>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
